import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessPublicToggleContext = createContext();

export const useBusinessPublicToggle = () => {
    const context = useContext(BusinessPublicToggleContext);
    if (!context) {
        throw new Error('useBusinessPublicToggle must be used within a BusinessPublicToggleProvider');
    }
    return context;
};

export const BusinessPublicToggleProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { makeAuthenticatedRequest, loginType } = useAuth();

    // API base URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

    // Valid toggle field names
    const VALID_TOGGLE_FIELDS = [
        'profile_public',
        'profile_products_public',
        'profile_services_public',
        'profile_phone_public',
        'profile_email_public',
        'profile_location_public',
        'profile_schedule_public',
        'profile_images_public',
        'profile_employees_public',
        'profile_events_public',
        'profile_visits_public',
    ];

    // Determine the correct base URL based on login type
    const getServiceBaseUrl = useCallback(() => {
        if (loginType === 'master') {
            return `${API_BASE_URL}/master/business-public-toggle`;
        } else if (loginType === 'admin') {
            return `${API_BASE_URL}/admin/business-public-toggle`;
        } else {
            throw new Error('Invalid login type for public profile management');
        }
    }, [API_BASE_URL, loginType]);

    // Helper function to handle API responses
    const handleApiResponse = async (response) => {
        const data = await response.json();

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(data.error || 'Business profile not found');
            } else if (response.status === 403) {
                // Specific handling for the "No Image" validation error
                throw new Error(data.error || 'Action forbidden: Requirements not met');
            } else if (response.status === 409) {
                // Specific handling for "Profile already exists"
                throw new Error(data.error || 'Profile already exists');
            } else if (response.status === 400) {
                throw new Error(data.error || 'Invalid request data');
            } else if (response.status === 500) {
                throw new Error(data.error || 'Internal server error');
            }
            throw new Error(data.error || data.message || 'Request failed');
        }

        return data;
    };

    // Clear error state
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Validate admin/master access
    const validateAccess = useCallback(() => {
        if (loginType !== 'admin' && loginType !== 'master') {
            throw new Error('Only admin and master users can manage public profile settings');
        }
    }, [loginType]);

    // Validate field name
    const validateFieldName = useCallback((fieldName) => {
        if (!VALID_TOGGLE_FIELDS.includes(fieldName)) {
            throw new Error(`Invalid field name: ${fieldName}. Valid fields are: ${VALID_TOGGLE_FIELDS.join(', ')}`);
        }
    }, []);

    // -------------------------------------------------------------------------
    // FETCH PROFILE STATUS
    // -------------------------------------------------------------------------
    const fetchProfileStatus = useCallback(async (businessRegistryNumber) => {
        validateAccess();

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            // Route: GET /public-toggle/{business_registry_number}
            const response = await makeAuthenticatedRequest(`${baseUrl}/public-toggle/${businessRegistryNumber}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const result = await handleApiResponse(response);
            // Returns { success, message, business_registry_number, fields: {...} }
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to fetch profile status';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // CREATE DEFAULT PROFILE
    // -------------------------------------------------------------------------
    const createDefaultProfile = useCallback(async (businessRegistryNumber) => {
        validateAccess();

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const payload = {
                business_registry_number: businessRegistryNumber
            };

            // Route: POST /public-toggle/create
            const response = await makeAuthenticatedRequest(`${baseUrl}/public-toggle/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await handleApiResponse(response);
            // Returns { success, message, business_registry_number, fields: {...}, updated_at }
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to create public profile';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // UPDATE SINGLE FIELD (TOGGLE)
    // -------------------------------------------------------------------------
    const updateProfileField = useCallback(async (businessRegistryNumber, fieldName, value) => {
        validateAccess();
        validateFieldName(fieldName);

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');
        if (typeof value !== 'boolean') throw new Error('Value must be a boolean');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const payload = {
                business_registry_number: businessRegistryNumber,
                field_name: fieldName,
                value: value
            };

            // Route: PUT /public-toggle
            const response = await makeAuthenticatedRequest(`${baseUrl}/public-toggle`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await handleApiResponse(response);
            // Returns { success, message, business_registry_number, fields: {fieldName: value}, updated_at }
            return result;
        } catch (err) {
            // This will catch the 403 Forbidden if they try to set true without an image
            const errorMessage = err.message || 'Failed to update profile field';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, validateFieldName, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // BULK UPDATE FIELDS
    // -------------------------------------------------------------------------
    const bulkUpdateProfileFields = useCallback(async (businessRegistryNumber, fields) => {
        validateAccess();

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');
        if (!fields || typeof fields !== 'object' || Object.keys(fields).length === 0) {
            throw new Error('Fields object is required and must contain at least one field');
        }

        // Validate all field names
        Object.keys(fields).forEach(fieldName => {
            validateFieldName(fieldName);
            if (typeof fields[fieldName] !== 'boolean') {
                throw new Error(`Value for ${fieldName} must be a boolean`);
            }
        });

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const payload = {
                business_registry_number: businessRegistryNumber,
                fields: fields
            };

            // Route: PUT /public-toggle/bulk
            const response = await makeAuthenticatedRequest(`${baseUrl}/public-toggle/bulk`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await handleApiResponse(response);
            // Returns { success, message, business_registry_number, fields: {...}, updated_at }
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to bulk update profile fields';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, validateFieldName, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // LEGACY: UPDATE PROFILE STATUS (for backward compatibility)
    // -------------------------------------------------------------------------
    const updateProfileStatus = useCallback(async (businessRegistryNumber, isPublic) => {
        // This is a wrapper around updateProfileField for backward compatibility
        return updateProfileField(businessRegistryNumber, 'profile_public', isPublic);
    }, [updateProfileField]);

    // -------------------------------------------------------------------------
    // CONVENIENCE METHODS FOR SPECIFIC FIELDS
    // -------------------------------------------------------------------------
    const toggleProfilePublic = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_public', value);
    }, [updateProfileField]);

    const toggleProfileProducts = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_products_public', value);
    }, [updateProfileField]);

    const toggleProfileServices = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_services_public', value);
    }, [updateProfileField]);

    const toggleProfilePhone = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_phone_public', value);
    }, [updateProfileField]);

    const toggleProfileEmail = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_email_public', value);
    }, [updateProfileField]);

    const toggleProfileLocation = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_location_public', value);
    }, [updateProfileField]);

    const toggleProfileSchedule = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_schedule_public', value);
    }, [updateProfileField]);

    const toggleProfileImages = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_images_public', value);
    }, [updateProfileField]);

    const toggleProfileEmployees = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_employees_public', value);
    }, [updateProfileField]);

    const toggleProfileEvents = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_events_public', value);
    }, [updateProfileField]);

    const toggleProfileVisits = useCallback(async (businessRegistryNumber, value) => {
        return updateProfileField(businessRegistryNumber, 'profile_visits_public', value);
    }, [updateProfileField]);

    // -------------------------------------------------------------------------
    // UTILITY & DEBUG
    // -------------------------------------------------------------------------

    const getEndpointInfo = useCallback(() => {
        return {
            loginType: loginType || 'none',
            managementUrl: loginType === 'master'
                ? `${API_BASE_URL}/master/business-public-toggle`
                : loginType === 'admin'
                    ? `${API_BASE_URL}/admin/business-public-toggle`
                    : 'N/A (requires login)',
            validFields: VALID_TOGGLE_FIELDS,
        };
    }, [loginType, API_BASE_URL]);

    const value = {
        // State
        loading,
        error,
        clearError,

        // Core Actions
        fetchProfileStatus,
        createDefaultProfile,
        updateProfileField,
        bulkUpdateProfileFields,

        // Legacy/Backward Compatibility
        updateProfileStatus,

        // Convenience Methods for Specific Fields
        toggleProfilePublic,
        toggleProfileProducts,
        toggleProfileServices,
        toggleProfilePhone,
        toggleProfileEmail,
        toggleProfileLocation,
        toggleProfileSchedule,
        toggleProfileImages,
        toggleProfileEmployees,
        toggleProfileEvents,
        toggleProfileVisits,

        // Utilities
        getEndpointInfo,
        validFields: VALID_TOGGLE_FIELDS,

        // Permissions
        hasManagementAccess: loginType === 'admin' || loginType === 'master',
    };

    return (
        <BusinessPublicToggleContext.Provider value={value}>
            {children}
        </BusinessPublicToggleContext.Provider>
    );
};